import { Popup, TagGroup } from "@hatch-digital-nl/abs-storybook/legacy";
import {
    setError,
    setFormValues,
    setIsAtQualification,
    setIsCreateCertificateModalOpen,
    setIsFunctionGroupModalOpen,
    setIsPostCreateCertificateSuccess,
    setIsQualificationStandardModalOpen,
    setSelectedQualification,
} from "../CreateCertificateModalSlice";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { History } from "../../../common/svg/History";
import { InfiniteImage } from "../../../common/svg/InfiniteImage";
import { ChangeEvent, JSX, useEffect, useRef, useState } from "react";
import { handleCreateCertificate } from "../utils/handleCreateCertificate";
import { useCreateCertificateMutation } from "../../../common/api/certificate";
import { useGetEmployeeMeQuery } from "../../../common/api/employee";
import { getValidityPeriodDisplay } from "../../../common/utils/getValidityPeriodDisplay";
import { ValidityPeriodIconEnum } from "../../../common/enums/ValidityPeriodIconEnum";
import { SubmitButtonComponent } from "../components/FormComponents/SubmitButtonComponent";
import { FileInputFieldComponent } from "../components/FormComponents/FileInputFieldComponent";
import { DateInputFieldComponent } from "../components/FormComponents/DateInputFieldComponent";
import { calculateExpiresAt } from "../utils/calculateExpiresAt";
import { setEmployeeValues } from "../utils/setEmployeeValues";
import { setQualificationValues } from "../utils/setQualificationValues";
import { handleTagClickOutside } from "../../../common/utils/handleTagClickOutside";
import { SelectFieldComponent } from "../../../common/components/SelectFieldComponent";
import { getEducatorOptions } from "../../../common/utils/selectOptions/getEducatorOptions";

export const EmployeeCreateFormAtQualification = (): JSX.Element => {
    const dispatch = useAppDispatch();
    const [file, setFile] = useState<File | null>(null);
    const functionGroupModalRef = useRef<HTMLDivElement>(null);
    const qualificationStandardModalRef = useRef<HTMLDivElement>(null);
    const {
        selectedQualification,
        formValues,
        error,
        isFunctionGroupModalOpen,
        isQualificationStandardModalOpen,
    } = useAppSelector((state) => state.createCertificateModal);
    const { data: getEmployeeMeData } = useGetEmployeeMeQuery({
        items_per_page: 9999,
    });
    const [
        postCreateCertificate,
        {
            isLoading: isPostCreateCertificateLoading,
            isSuccess: isPostCreateCertificateSuccess,
        },
    ] = useCreateCertificateMutation();

    useEffect(() => {
        setEmployeeValues({
            formValues,
            selectedEmployee: {
                id: getEmployeeMeData?.id,
                user: getEmployeeMeData?.user,
            },
            dispatch,
        });
        setQualificationValues({
            formValues,
            selectedQualification,
            dispatch,
        });
    }, [formValues, selectedQualification, getEmployeeMeData, dispatch]);

    useEffect(() => {
        calculateExpiresAt({
            formValues,
            dispatch,
        });
    }, [
        formValues,
        formValues?.validAt,
        formValues?.qualification?.validityPeriod,
        formValues?.qualification?.validityType,
        formValues?.qualification?.fixedValidUntil,
        dispatch,
    ]);

    useEffect(() => {
        if (!isPostCreateCertificateSuccess) {
            dispatch(setIsPostCreateCertificateSuccess(false));
        }
    }, [isPostCreateCertificateSuccess, dispatch]);

    useEffect(() => {
        const clickOutsideHandler = (event: MouseEvent): void => {
            handleTagClickOutside({
                functionGroupModalRef,
                qualificationStandardModalRef,
                isFunctionGroupModalOpen,
                isQualificationStandardModalOpen,
                setIsFunctionGroupModalOpen,
                setIsQualificationStandardModalOpen,
                dispatch,
                event,
            });
        };

        document.addEventListener("mousedown", clickOutsideHandler);

        return () => {
            document.removeEventListener("mousedown", clickOutsideHandler);
        };
    }, [isFunctionGroupModalOpen, isQualificationStandardModalOpen, dispatch]);

    const validityPeriod = getValidityPeriodDisplay({
        validityPeriod: formValues?.qualification?.validityPeriod,
        validityType: formValues?.qualification?.validityType,
        fixedValidUntil: formValues?.qualification?.fixedValidUntil,
    });

    return (
        <Popup
            className={
                "c-create-certificate-modal c-create-certificate-modal--at-qualification"
            }
            title={"Allround automonteur"}
            onCloseClick={() => {
                dispatch(setIsCreateCertificateModalOpen(false));
                dispatch(setIsAtQualification(false));
                dispatch(setSelectedQualification(null));
                dispatch(setFormValues(null));
                dispatch(setError(null));
            }}
        >
            <form
                className={"c-popup__content__body"}
                onSubmit={(event) => {
                    event.preventDefault();
                    if (!isPostCreateCertificateLoading) {
                        handleCreateCertificate({
                            formValues,
                            file,
                            postCreateCertificate,
                            dispatch,
                        });
                    }
                }}
            >
                <div className={"c-create-certificate-modal__header"}>
                    <TagGroup
                        tags={selectedQualification.function_group}
                        tagColor={"#1D2D58"}
                        tagBackgroundColor={"#DAEBA8"}
                        isOpen={isFunctionGroupModalOpen}
                        handleClick={() =>
                            dispatch(
                                setIsFunctionGroupModalOpen(
                                    !isFunctionGroupModalOpen,
                                ),
                            )
                        }
                        modalRef={functionGroupModalRef}
                    />
                    <TagGroup
                        tags={selectedQualification.qualification_standards}
                        tagColor={"#FFFFFF"}
                        tagBackgroundColor={"#07A9D6"}
                        isOpen={isQualificationStandardModalOpen}
                        handleClick={() =>
                            dispatch(
                                setIsQualificationStandardModalOpen(
                                    !isQualificationStandardModalOpen,
                                ),
                            )
                        }
                        modalRef={qualificationStandardModalRef}
                    />
                </div>
                <div className={"c-create-certificate-modal__validity-period"}>
                    <div
                        className={
                            "c-create-certificate-modal__validity-period__inner"
                        }
                    >
                        {validityPeriod?.icon ===
                        ValidityPeriodIconEnum.INFINITE ? (
                            <InfiniteImage />
                        ) : (
                            <History />
                        )}
                        <p>{validityPeriod?.text}</p>
                    </div>
                </div>
                {error && <div className={"c-error"}>{error}</div>}
                <SelectFieldComponent
                    id={1}
                    options={getEducatorOptions(
                        selectedQualification.educators,
                    )}
                    onChange={(newValue) => {
                        dispatch(
                            setFormValues({
                                ...formValues,
                                educator: {
                                    selectedId: newValue.value,
                                    selectedValue: newValue.label,
                                },
                            }),
                        );
                    }}
                    value={{
                        value: formValues?.educator?.selectedId || "",
                        label: formValues?.educator?.selectedValue || "",
                    }}
                    errorMessage={"Opleider is verplicht"}
                    placeHolder={"Selecteer opleider..."}
                    title={"Selecteer opleider"}
                />
                <DateInputFieldComponent
                    id={1}
                    value={formValues?.validAt}
                    expiresAt={formValues?.expiresAt}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                        dispatch(
                            setFormValues({
                                ...formValues,
                                validAt: event.target.value,
                            }),
                        );
                    }}
                />
                <FileInputFieldComponent
                    id={1}
                    formValues={formValues}
                    dispatch={dispatch}
                    setFile={setFile}
                    showNotice={true}
                    noticeText={
                        "Nadat je jouw certificaat hebt toegevoegd, dient deze nog door de vestigingsmanager te worden goedgekeurd"
                    }
                />
                <SubmitButtonComponent
                    isPostCreateCertificateLoading={
                        isPostCreateCertificateLoading
                    }
                />
            </form>
        </Popup>
    );
};
